import { useEffect, useState } from 'react';
import Widget from '../../../components/Dashboard/Widget';
import { useDashboardControls } from 'contexts/Dashboard/useDashboardControls';
import { KPIsWidgetEnum, LongTitles } from '../utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faSpinner } from '@fortawesome/free-solid-svg-icons';
import Button from 'components/inputs/Button';
import { showException } from 'utils/exception-helper';
import { downloadBlob } from 'utils/file-utils';
import { formatDate } from 'utils/date-helpers';
import { useKPIGetETAAccuracy } from 'api/kpi/kpi';
import { useExportExportETAAccuracyHook } from 'api/export/export';
import { maxKPIDateRangeMs } from 'constants/AppConstants';

const StatBox = ({
  label,
  value,
  count,
  color,
}: {
  label: string;
  value: string;
  count: number;
  color: string;
}) => (
  <div
    style={{
      textAlign: 'center',
      padding: '15px',
      backgroundColor: `${color}15`,
      borderRadius: '8px',
      border: `2px solid ${color}`,
    }}
  >
    <div style={{ fontSize: '28px', fontWeight: 'bold', color }}>{value}</div>
    <div style={{ fontSize: '14px', fontWeight: '500', marginTop: '5px' }}>
      {label}
    </div>
    <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
      ({count} st)
    </div>
  </div>
);

const ETAAccuracyWidget = () => {
  const [isDownloading, setIsDownloading] = useState(false);

  const {
    dashboardState: {
      daterange,
      selectedSupplierIDs,
      selectedCustomerNames,
      selectedFromCountyIDs,
      selectedToCountyIDs,
      excludeCompanyNameContains,
    },
  } = useDashboardControls();

  const getETAAccuracyData = useKPIGetETAAccuracy();

  const startDate = daterange[0];
  const endDate = daterange[1];

  const startDateTime = startDate ? startDate.getTime() : null;
  const endDateTime = endDate ? endDate.getTime() : null;

  const isDateRangeTooLong =
    startDate &&
    endDate &&
    endDate.getTime() - startDate.getTime() > maxKPIDateRangeMs;

  useEffect(() => {
    if (startDate && endDate && !isDateRangeTooLong) {
      getETAAccuracyData.mutate({
        data: {
          startDate,
          endDate,
          supplierIDs: selectedSupplierIDs,
          customerNames: selectedCustomerNames,
          fromCountyIDs: selectedFromCountyIDs,
          toCountyIDs: selectedToCountyIDs,
          excludeCompanyNameContains: excludeCompanyNameContains ?? undefined,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    startDateTime,
    endDateTime,
    selectedSupplierIDs,
    selectedCustomerNames,
    selectedFromCountyIDs,
    selectedToCountyIDs,
    excludeCompanyNameContains,
    isDateRangeTooLong,
  ]);

  const downloadExcel = useExportExportETAAccuracyHook();

  const { data } = getETAAccuracyData;

  return (
    <Widget
      title={LongTitles[KPIsWidgetEnum.ETAAccuracy]}
      count={undefined}
      isLoading={getETAAccuracyData.isLoading}
      info="Jämför Google ETA-prediktion (skapad ≤60 min före ankomst) med faktisk ankomst. I tid = ±5 min."
    >
      <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
        <Button
          disabled={isDownloading || !startDate || !endDate}
          onClick={async () => {
            try {
              setIsDownloading(true);
              const result = await downloadExcel({
                startDate: daterange[0]!,
                endDate: daterange[1]!,
                supplierIDs: selectedSupplierIDs,
                customerNames: selectedCustomerNames,
                fromCountyIDs: selectedFromCountyIDs,
                toCountyIDs: selectedToCountyIDs,
                excludeCompanyNameContains: excludeCompanyNameContains ?? undefined,
              });
              if (result) {
                downloadBlob(
                  result,
                  `Nyckeltal_ETAPrecision_${formatDate(daterange[0]!)}_${formatDate(daterange[1]!)}.xlsx`
                );
              }
            } catch (error) {
              showException(error);
            } finally {
              setIsDownloading(false);
            }
          }}
        >
          <FontAwesomeIcon icon={faDownload} /> Ladda ner
          {isDownloading ? (
            <FontAwesomeIcon
              icon={faSpinner}
              spin
              style={{ marginLeft: '5px' }}
            />
          ) : null}
        </Button>
      </div>

      {isDateRangeTooLong ? (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <p>
            Datumintervallet är för långt för att visa data. Använd
            nedladdningsknappen för att exportera data.
          </p>
        </div>
      ) : !data || data.totalAssignmentsWithETA === 0 ? (
        <div style={{ textAlign: 'center', marginTop: '20px' }}>
          {getETAAccuracyData.isLoading ? (
            <FontAwesomeIcon icon={faSpinner} spin />
          ) : (
            <p>Ingen ETA-data att visa för vald filtrering.</p>
          )}
        </div>
      ) : (
        <div style={{ padding: '10px' }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '15px',
              marginBottom: '20px',
            }}
          >
            <StatBox
              label="I tid"
              value={`${data.onTimePercentage}%`}
              count={data.onTimeCount}
              color="#28a745"
            />
            <StatBox
              label="Sena"
              value={`${data.latePercentage}%`}
              count={data.lateCount}
              color="#dc3545"
            />
            <StatBox
              label="Tidiga"
              value={`${data.earlyPercentage}%`}
              count={data.earlyCount}
              color="#17a2b8"
            />
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '15px',
              borderTop: '1px solid #eee',
              paddingTop: '15px',
            }}
          >
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold' }}>
                {data.averageDeviationMinutes > 0 ? '+' : ''}
                {data.averageDeviationMinutes} min
              </div>
              <div style={{ fontSize: '12px', color: '#666' }}>
                Snittavvikelse
              </div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold' }}>
                {data.medianDeviationMinutes > 0 ? '+' : ''}
                {data.medianDeviationMinutes} min
              </div>
              <div style={{ fontSize: '12px', color: '#666' }}>
                Medianavvikelse
              </div>
            </div>
          </div>

          <div
            style={{
              textAlign: 'center',
              marginTop: '15px',
              fontSize: '12px',
              color: '#888',
            }}
          >
            Baserat på {data.totalAssignmentsWithETA} uppdrag med ETA-data
          </div>
        </div>
      )}
    </Widget>
  );
};

export default ETAAccuracyWidget;
